import { useState } from "react";
import { Shield, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-12 bg-[#0b1329]">
      <div className="hidden lg:flex lg:col-span-7 relative overflow-hidden flex-col items-center justify-center gap-10 px-12 py-16 bg-[radial-gradient(ellipse_at_center,#1b2160_0%,#0b1329_70%)]">
        <img src="/brand/portada-mapa.webp" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(11,19,41,0.45)_0%,rgba(11,19,41,0.7)_80%)]" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0b1329]" />
        <div className="absolute w-[34rem] h-[34rem] rounded-full bg-violet-600/20 blur-3xl" />
        <picture className="relative rise">
          <source srcSet="/brand/eventpol-logo-512.webp 512w, /brand/eventpol-logo-1024.webp 1024w" sizes="26rem" type="image/webp" />
          <img src="/brand/eventpol-logo-512.png" alt="EventPOL · Gestión de eventos policiales · by Aprendendo" width="512" height="512" className="w-[26rem] max-w-full h-auto drop-shadow-[0_25px_45px_rgba(76,29,149,0.45)]" data-testid="login-hero-logo" />
        </picture>
        <div className="relative max-w-lg text-center rise">
          <div className="font-mono text-xs tracking-[0.3em] text-amber-400 mb-3">POLICÍAS DE GALICIA</div>
          <h1 className="font-display text-4xl font-extrabold leading-tight">Planifica cortes, desvíos y efectivos en minutos.</h1>
          <p className="text-slate-300 mt-4 text-sm leading-relaxed">
            Marca en el mapa el recorrido, los cortes y las zonas a despejar. El sistema calcula los desvíos, el personal necesario y los relevos de cada agente.
          </p>
        </div>
      </div>
      <div className="lg:col-span-5 flex items-center justify-center p-6 sm:p-8 bg-slate-50 text-slate-900 min-h-screen lg:min-h-0">
        <form onSubmit={submit} className="w-full max-w-sm rise" data-testid="login-form">
          <img src="/brand/eventpol-logo-512.webp" alt="EventPOL · Gestión de eventos policiales · by Aprendendo" width="512" height="512" className="lg:hidden w-40 h-40 mx-auto mb-8" data-testid="login-mobile-logo" />
          <div className="hidden lg:flex items-center gap-4 mb-8" data-testid="login-brand">
            <img src="/brand/eventpol-logo-192.png" alt="EventPOL · Gestión de eventos policiales" className="w-20 h-20 rounded-2xl shadow-lg" data-testid="login-logo" />
            <div className="leading-tight">
              <div className="font-display text-2xl font-bold text-slate-900">EventPOL</div>
              <div className="text-sm text-violet-700">por Aprendendo · Consultora de Formación</div>
            </div>
          </div>
          <h2 className="font-display text-3xl font-bold">Control de acceso</h2>
          <p className="text-slate-500 text-sm mt-1 mb-8 flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-slate-500" /> Policías de Galicia · Gestión de tráfico y eventos</p>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs text-slate-600">Usuario o correo electrónico</Label>
              <Input id="email" type="text" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="bg-white h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw" className="text-xs text-slate-600">Contraseña</Label>
              <Input id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="bg-white h-11" />
            </div>
            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy} className="w-full h-11 font-semibold" data-testid="login-submit-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Acceder"}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-6">Las cuentas las crea el administrador. Perfiles: Administrador, Usuario y Observador.</p>
          <p className="text-[0.6875rem] text-slate-500 mt-8 pt-4 border-t border-slate-200" data-testid="login-copyright">© {new Date().getFullYear()} EventPOL · Aprendendo - Consultora de Formación. Software con licencia de uso.</p>
        </form>
      </div>
    </div>
  );
}
